import { FC, useState } from 'react';
import { IonButton } from '@ionic/react';
import { useSwiper } from 'swiper/react';
import { useInstance } from '@meraki-internal/react-dependency-injection';
import { useIsNative } from '../utils/useIsNative';
import { ProfileCollectionForm, IProfileCollectionData } from '../user/ProfileCollectionForm';
import { PreAuthProfileService } from '../user/PreAuthProfileService';
import { IConsentRecord, UserState } from '../user/UserState';
import { SMS_CONSENT_VERSION, SMS_CONSENT_CREATE_TEXT, SMS_CONSENT_CONTINUE_TEXT } from '../user/consentText';
import { WelcomeSlide } from './WelcomeSlide';
import { SlideActions } from './SlideActions';
import { SlideButton } from './SlideButton';
import { Text } from '../components/Text';
import { MixPanelEventEmitter } from '../metrics/MixPanelEventEmitter';
import { IntroScreenState } from '../home/IntroScreenState';
import { IDoneHandler, ISignIn } from './WelcomeTypes';

export const PROFILE_SLIDE_HEADING = "Hi, I'm Bea";

const buildInitialProfileData = (userState: UserState, isAnonymous: boolean): IProfileCollectionData => {
    if (!isAnonymous) {
        const rawPhone = userState.state.phone || '';
        const formattedPhone = rawPhone.length === 10
            ? `${rawPhone.slice(0, 3)}-${rawPhone.slice(3, 6)}-${rawPhone.slice(6)}`
            : rawPhone;
        return {
            firstName: userState.state.firstName || '',
            lastName: userState.state.lastName || '',
            zipCode: userState.state.address || '',
            phone: formattedPhone
        };
    }
    return { firstName: '', lastName: '', zipCode: '', phone: '' };
};

const buildSmsConsent = ({ phone, disclosureText }: { phone: string, disclosureText: string }): IConsentRecord => ({
    consentType: 'sms-consent',
    consentedAt: new Date().toISOString(),
    disclosureVersion: SMS_CONSENT_VERSION,
    disclosureText,
    phone: phone.replace(/\D/g, '')
});

interface IProfileSlideProps {
    onDone: IDoneHandler;
    signIn?: ISignIn;
    isAnonymous: boolean;
    keyboardOpen?: boolean;
}

export const ProfileSlide: FC<IProfileSlideProps> = ({ onDone, signIn, isAnonymous, keyboardOpen }) => {
    const mixpanel = useInstance(MixPanelEventEmitter);
    const preAuthProfile = useInstance(PreAuthProfileService);
    const userState = useInstance(UserState);
    const introScreenState = useInstance(IntroScreenState);
    const isNative = useIsNative();
    const swiper = useSwiper();

    const [profileData, setProfileData] = useState<IProfileCollectionData>(() => buildInitialProfileData(userState, isAnonymous));
    const [profileValid, setProfileValid] = useState(false);

    const applyProfileAndSave = async ({ consent }: { consent?: IConsentRecord } = {}) => {
        await preAuthProfile.applyToUserState({ data: profileData, location: 'welcome', consent, storeToStorage: isAnonymous });
        if (!isAnonymous) {
            await userState.save();
        }
    };

    const getFieldsEntered = (): string[] => [
        profileData.firstName ? 'firstName' : '',
        profileData.lastName ? 'lastName' : '',
        profileData.zipCode ? 'zipCode' : '',
        profileData.phone ? 'phone' : ''
    ].filter(Boolean);

    const onTryWithoutAccount = async () => {
        mixpanel.track('Profile Collection Skipped', () => ({ Location: 'welcome' }));
        await onDone({ hide: true, trackingFrom: 'Try Without Account', fieldsEntered: getFieldsEntered() });
    };

    const onCreateAccount = async () => {
        const consent = buildSmsConsent({ phone: profileData.phone, disclosureText: SMS_CONSENT_CREATE_TEXT });
        await applyProfileAndSave({ consent });
        await onDone({ hide: false, trackingFrom: 'Create Account', fieldsEntered: getFieldsEntered() });
        await signIn!({ trackingFrom: 'Welcome Create Account', returnTo: '/caregiver?intro=true' });
    };

    const onContinue = async () => {
        const consent = buildSmsConsent({ phone: profileData.phone, disclosureText: SMS_CONSENT_CONTINUE_TEXT });
        await applyProfileAndSave({ consent });
        introScreenState.show();
        await onDone({ hide: true, trackingFrom: 'Continue', fieldsEntered: getFieldsEntered() });
    };

    const smsConsentText = signIn ? SMS_CONSENT_CREATE_TEXT : SMS_CONSENT_CONTINUE_TEXT;

    return (
        <WelcomeSlide image="6-profile.webp" compactImage keyboardOpen={keyboardOpen}>
            <div style={{width: '100%'}}>
                <div style={{fontSize: 24, fontWeight: 'bold', textAlign: 'center'}}>Hi, I'm Bea</div>
                <div style={{padding: '8px 0 16px', textAlign: 'center'}}>
                    <Text type="detail">Let's get to know each other.</Text>
                </div>
                <ProfileCollectionForm
                    data={profileData}
                    onChange={setProfileData}
                    onValidityChange={setProfileValid}
                />
            </div>
            <SlideActions>
                {!isNative &&
                    <IonButton fill="clear" type="button" onClick={() => swiper.slidePrev()}>&lt; Back</IonButton>
                }
                {signIn &&
                    <div style={{textAlign: 'center', paddingTop: 8}}>
                        <span style={{fontSize: 13, color: 'var(--ion-color-primary)', textDecoration: 'underline', cursor: 'pointer'}} onClick={onTryWithoutAccount}>Try without account</span>
                    </div>
                }
                <div style={{textAlign: 'center', padding: '12px 0 0', fontSize: 12, color: 'var(--ion-color-step-700)'}}>
                    {smsConsentText}
                </div>
                {signIn ? (
                    <SlideButton onClick={onCreateAccount} disabled={!profileValid}>Create account</SlideButton>
                ) : (
                    <SlideButton onClick={onContinue} disabled={!profileValid}>Continue</SlideButton>
                )}
            </SlideActions>
        </WelcomeSlide>
    );
};
